import { useCallback } from 'react';
import useSWR from 'swr';
import get from 'lodash/get';
import queryString from 'query-string';
import { useSearchParams } from 'react-router';
import ProductDetails from '../../components/product-details/ProductDetails';
import ProductList from '../../components/product-list/ProductList';
import ProductListItem from '../../components/product-list-item/ProductListItem';
import config from '../../config';

function PageProduct({ lang }) {
  // Fetcher with retry logic (5 attempts with 1 second delay)
  const fetcher = useCallback(async (url) => {
    const maxRetries = 5;
    let lastError;

    for (let i = 0; i < maxRetries; i++) {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
      } catch (error) {
        lastError = error;
        console.error(`Fetch attempt ${i + 1} failed:`, error.message);
        if (i < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }
    throw lastError;
  }, []);

  const [searchParams] = useSearchParams();
  const productId = searchParams.get('id');

  const apiEndpoint = config.apiEndpoint;
  const isEnglish = lang === 'en';
  const fieldPostfix = isEnglish ? 'EN' : 'TC';

  // Product details
  const { data: productData, error: productError, isLoading: productLoading } = useSWR(
    productId ? `${apiEndpoint}/api/products/${productId}?${queryString.stringify({
      sort: 'ordering',
      populate: ['category', /* 'productCategories', */ 'coverImage', 'image' /*, 'button' */],
    })}` : null,
    fetcher
  );
  const productInfo = get(productData, 'data');

  // Other products in the same category
  const categoryId = get(productInfo, 'category.id');
  const { data: categoryProductsData, error: categoryError } = useSWR(
    productInfo ? `${apiEndpoint}/api/products?${queryString.stringify({
      fields: [
        `title${fieldPostfix}`,
        `school${fieldPostfix}`,
        'votes',
        'voteBatch',
      ],
      populate: ['coverImage'],
      'filters[category][id]': categoryId,
      'filters[documentId][$ne]': productId,
      sort: 'ordering',
      limit: 4,
    })}` : null,
    fetcher
  );
  const categoryProductsInfo = get(categoryProductsData, 'data');

  // Handle loading state
  if (productLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>{isEnglish ? 'Loading...' : '載入中...'}</p>
      </div>
    );
  }

  // Handle error state
  if (productError) {
    return (
      <div style={{ padding: '20px' }}>
        <div className="error">
          <p>{isEnglish ? 'Error loading student team details. Please try again later.' : '載入學生隊伍詳情時發生錯誤，請稍後再試。'}</p>
          {productError.message && <p>Error: {productError.message}</p>}
        </div>
      </div>
    );
  }

  // Handle no product found
  if (!productInfo) {
    return (
      <div style={{ padding: '20px' }}>
        <div className="error">
          <p>{isEnglish ? 'Unable to load student team details. Please try again later.' : '無法載入學生隊伍詳情，請稍後再試。'}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb: All / Category / School Name - matches WordPress format */}
      {productInfo && (
        <div style={{ marginBottom: '20px', fontSize: '14px', color: '#666', marginTop: '20px', paddingLeft: '10px' }}>
          <a href={config.basePath || '/'} style={{ color: '#666', textDecoration: 'none' }}>
            {isEnglish ? 'All' : '全部'}
          </a>
          {productInfo.category && (
            <>
              <span style={{ margin: '0 5px' }}>/</span>
              <a href={`${config.basePath || ''}/product-category/?id=${productInfo.category.documentId}`} style={{ color: '#666', textDecoration: 'none' }}>
                {isEnglish ? productInfo.category.nameEN : productInfo.category.nameTC}
              </a>
            </>
          )}
          <span style={{ margin: '0 5px' }}>/</span>
          <span style={{ fontWeight: 500, color: '#333' }}>
            {get(productInfo, `school${fieldPostfix}`) || (isEnglish ? 'Current Student Team' : '當前學生隊伍')}
          </span>
        </div>
      )}

      {productInfo && (
        <ProductDetails
          product={productInfo}
          lang={lang}
        />
      )}

      {categoryError && (
        <div style={{ padding: '20px' }}>
          <p>{isEnglish ? 'Unable to load other teams.' : '無法載入其他隊伍。'}</p>
        </div>
      )}

      {!categoryError && (
        <>
          <ProductList label={isEnglish ? 'Other Teams' : '其他團隊'}>
            {categoryProductsInfo && categoryProductsInfo.length > 0 && (
              categoryProductsInfo.map((item, index) => (
                <ProductListItem
                  key={index}
                  product={item}
                  lang={lang}
                />
              ))
            )}
          </ProductList>
          {!(categoryProductsInfo && categoryProductsInfo.length > 0) && (
            <p style={{ textAlign: 'center', padding: '20px', marginBottom: '20px' }}>
              {isEnglish ? 'No other teams found.' : '沒有找到其他隊伍。'}
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default PageProduct;
